import React, { useEffect, useRef, useState } from 'react';
import { UploadCloud, FileText, Plus, X, CheckCircle, Loader2 } from 'lucide-react';
import { uploadReimbursementFile } from '../../../Services/APIs/reimbursement';
import { PROOF_FILE, proofFileProblem, formatFileSize, proofRequiredMessage } from './reimbursementStatus';

// The member's next step once their Reimbursement Prescription is approved. The invoice / receipt
// dropped here uploads straight away; "Upload Proof of Treatment" then opens the proof form with it
// attached, to add the treatment details, a post-treatment photo and payout details (all required
// by the API). `uploaded` ({ file, url }) is kept by the section, so the file survives the member
// opening and closing that form.
const ProofOfTreatmentCard = ({ reimbursement, uploaded, onUploaded, onContinue }) => {
    const [upload, setUpload] = useState(() => (uploaded ? { ...uploaded, status: 'done', progress: 100, error: null } : null));
    const [problem, setProblem] = useState('');
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    // Each pick or removal starts a new attempt; a late answer from an older one is ignored.
    const attempt = useRef(0);
    const controller = useRef(null);

    useEffect(() => () => controller.current?.abort(), []);

    const start = async (file) => {
        if (!file) return;
        const reason = proofFileProblem(file);
        if (reason) {
            setProblem(reason);
            return;
        }
        setProblem('');
        controller.current?.abort();
        attempt.current += 1;
        const current = attempt.current;
        const abort = new AbortController();
        controller.current = abort;
        setUpload({ file, status: 'uploading', progress: 0, url: null, error: null });
        onUploaded(null);

        try {
            const response = await uploadReimbursementFile(file, { type: 'claim', field: 'invoice_receipt' }, {
                signal: abort.signal,
                onUploadProgress: (event) => {
                    if (attempt.current !== current || !event.total) return;
                    const progress = Math.min(99, Math.round((event.loaded / event.total) * 100));
                    setUpload((prev) => (prev ? { ...prev, progress } : prev));
                },
            });
            if (attempt.current !== current) return;
            const url = response.data?.data?.url;
            if (!url) throw new Error('The upload returned no file.');
            setUpload({ file, status: 'done', progress: 100, url, error: null });
            onUploaded({ file, url });
        } catch (err) {
            if (attempt.current !== current) return;
            const error = err?.response?.data?.message || 'Upload failed. Please try again.';
            setUpload({ file, status: 'error', progress: 0, url: null, error });
        }
    };

    const remove = () => {
        attempt.current += 1;
        controller.current?.abort();
        controller.current = null;
        setUpload(null);
        setProblem('');
        onUploaded(null);
    };

    const onDrop = (event) => {
        event.preventDefault();
        setDragging(false);
        start(event.dataTransfer?.files?.[0]);
    };

    const ready = upload?.status === 'done';
    const showDropzone = !upload || upload.status === 'error';

    return (
        <div className="border-2 border-purple-200 bg-purple-50 rounded-xl p-4 mb-3" data-testid="proof-card">
            <div className="flex items-start gap-3 mb-3">
                <span className="w-9 h-9 rounded-full bg-purple-600 text-white flex items-center justify-center flex-shrink-0">
                    <UploadCloud className="w-5 h-5" />
                </span>
                <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900">Proof of Treatment Required</p>
                    <p className="text-xs text-gray-600 mt-0.5" data-testid="proof-required-message">{proofRequiredMessage(reimbursement)}</p>
                </div>
            </div>

            {upload && (
                <div className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg p-3 mb-3" data-testid="proof-file">
                    <FileText className="w-5 h-5 text-purple-500 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-800 truncate" data-testid="proof-file-name">{upload.file.name}</p>
                        <p className="text-xs text-gray-400">{formatFileSize(upload.file.size)}</p>
                        {upload.status === 'uploading' && (
                            <>
                                <div className="h-1.5 bg-purple-100 rounded-full mt-2 overflow-hidden">
                                    <div className="h-full bg-purple-600 rounded-full transition-all" style={{ width: `${upload.progress}%` }} />
                                </div>
                                <p className="flex items-center gap-1.5 text-xs text-gray-500 mt-1">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading… {upload.progress}%
                                </p>
                            </>
                        )}
                        {upload.status === 'done' && (
                            <p className="flex items-center gap-1 text-xs font-semibold text-green-700 mt-1" data-testid="proof-file-uploaded">
                                <CheckCircle className="w-3.5 h-3.5" /> Uploaded
                            </p>
                        )}
                        {upload.status === 'error' && <p role="alert" className="text-xs text-red-600 mt-1">{upload.error}</p>}
                    </div>
                    <button
                        type="button"
                        onClick={remove}
                        aria-label={`Remove ${upload.file.name}`}
                        data-testid="proof-remove"
                        className="w-7 h-7 rounded-full flex items-center justify-center text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            )}

            {showDropzone && (
                <div
                    data-testid="proof-dropzone"
                    onDragEnter={(event) => { event.preventDefault(); setDragging(true); }}
                    onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    className={`border-2 border-dashed rounded-xl p-4 text-center mb-3 transition-colors ${dragging ? 'border-purple-500 bg-purple-100' : 'border-purple-200 bg-white'}`}
                >
                    <UploadCloud className="w-7 h-7 text-purple-500 mx-auto mb-1" />
                    <p className="text-sm font-semibold text-gray-800">Upload proof of treatment</p>
                    <p className="text-xs text-gray-500 mt-0.5">Upload your treatment receipt, invoice, treatment summary, or other supporting document.</p>
                    <p className="text-xs text-gray-400 mt-2">Drag and drop a file here, or</p>
                    <button
                        type="button"
                        onClick={() => inputRef.current?.click()}
                        className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors"
                    >
                        <Plus className="w-3.5 h-3.5" /> Upload proof
                    </button>
                    <p className="text-[11px] text-gray-400 mt-2">{PROOF_FILE.hint}</p>
                    <input
                        ref={inputRef}
                        type="file"
                        accept={PROOF_FILE.accept}
                        className="hidden"
                        data-testid="proof-file-input"
                        aria-label="Upload proof of treatment"
                        onChange={(event) => {
                            start(event.target.files?.[0]);
                            // Lets the member pick the same file again after removing it.
                            event.target.value = '';
                        }}
                    />
                </div>
            )}

            {problem && <p role="alert" className="text-xs text-red-600 -mt-1 mb-3">{problem}</p>}

            <button
                type="button"
                disabled={!ready}
                onClick={() => onContinue({ file: upload.file, url: upload.url })}
                data-testid="upload-proof-of-treatment"
                className="w-full py-3 text-sm font-semibold text-white bg-purple-600 hover:bg-purple-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-purple-600"
            >
                Upload Proof of Treatment
            </button>
            <p className="text-[11px] text-gray-500 text-center mt-1.5">
                {ready
                    ? 'Next, add your treatment details, a post-treatment photo and where to send your payout.'
                    : 'Add your invoice or receipt above to continue.'}
            </p>
        </div>
    );
};

export default ProofOfTreatmentCard;
